import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { 
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter 
} from '@/components/ui/dialog';
import { 
  Plus, Edit, Trash2, Eye, EyeOff, ExternalLink, GripVertical, 
  Loader2, Save, X, ChevronDown, ChevronUp 
} from 'lucide-react';

const PlatformManagement = () => {
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPlatform, setEditingPlatform] = useState(null);
  const [expandedPlatform, setExpandedPlatform] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    display_name: '',
    icon: '🎟️',
    login_url: '',
    transfer_type: '',
    steps: [''],
    after_transfer: [''],
    note: '',
    note_type: 'info',
    is_active: true,
    order: 0
  });

  const fetchPlatforms = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/platforms`);
      setPlatforms(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des plateformes');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlatforms();
  }, []);

  const resetForm = () => {
    setFormData({
      name: '',
      display_name: '',
      icon: '🎟️',
      login_url: '',
      transfer_type: '',
      steps: [''],
      after_transfer: [''],
      note: '',
      note_type: 'info',
      is_active: true,
      order: platforms.length
    });
    setEditingPlatform(null);
  };

  const handleOpenForm = (platform = null) => {
    if (platform) {
      setEditingPlatform(platform);
      setFormData({
        name: platform.name || '',
        display_name: platform.display_name || '',
        icon: platform.icon || '🎟️',
        login_url: platform.login_url || '',
        transfer_type: platform.transfer_type || '',
        steps: platform.steps?.length > 0 ? platform.steps : [''],
        after_transfer: platform.after_transfer?.length > 0 ? platform.after_transfer : [''],
        note: platform.note || '',
        note_type: platform.note_type || 'info',
        is_active: platform.is_active !== false,
        order: platform.order || 0
      });
    } else {
      resetForm();
    }
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    resetForm();
  };

  const handleAddStep = (field) => {
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field], '']
    }));
  };

  const handleRemoveStep = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const handleStepChange = (field, index, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].map((step, i) => i === index ? value : step)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const data = {
        ...formData,
        steps: formData.steps.filter(s => s.trim() !== ''),
        after_transfer: formData.after_transfer.filter(s => s.trim() !== ''),
        note: formData.note || null
      };

      if (editingPlatform) {
        await axios.put(`${API}/admin/platforms/${editingPlatform.id}`, data);
        toast.success('Plateforme mise à jour avec succès');
      } else {
        await axios.post(`${API}/admin/platforms`, data);
        toast.success('Plateforme créée avec succès');
      }

      fetchPlatforms();
      handleCloseForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (platform) => {
    try {
      await axios.patch(`${API}/admin/platforms/${platform.id}/toggle`);
      toast.success(`Plateforme ${platform.is_active ? 'masquée' : 'activée'}`);
      fetchPlatforms();
    } catch (error) {
      toast.error('Erreur lors de la modification');
    }
  };

  const handleDelete = async (platform) => {
    try {
      await axios.delete(`${API}/admin/platforms/${platform.id}`);
      toast.success('Plateforme supprimée');
      setDeleteConfirm(null);
      fetchPlatforms();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const iconOptions = ['🎟️', '📱', '🎵', '🇨🇦', '🎭', '🎫', '🎪', '🎤', '🏟️', '🎸', '🎹', '🎺'];
  const noteTypes = [
    { value: 'success', label: '🟢 Succès', color: 'bg-green-100 text-green-800' },
    { value: 'warning', label: '⚠️ Attention', color: 'bg-amber-100 text-amber-800' },
    { value: 'info', label: 'ℹ️ Info', color: 'bg-blue-100 text-blue-800' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-[#67ffbb]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Plateformes de billets</h2>
          <p className="text-sm text-gray-500">{platforms.length} plateforme(s) configurée(s)</p>
        </div>
        <Button
          onClick={() => handleOpenForm()}
          className="bg-[#67ffbb] hover:bg-[#4de6a8] text-gray-900 font-semibold"
        >
          <Plus className="h-4 w-4 mr-2" />
          Ajouter une plateforme
        </Button>
      </div>

      {/* Platforms List */}
      <div className="space-y-3">
        {platforms.map((platform) => (
          <div 
            key={platform.id}
            className={`bg-white rounded-xl border-2 transition-all ${
              platform.is_active 
                ? 'border-gray-200 hover:border-[#67ffbb]' 
                : 'border-gray-100 bg-gray-50 opacity-60'
            }`}
          >
            {/* Platform Header */}
            <div className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-2xl">{platform.icon}</span>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-gray-900">{platform.display_name || platform.name}</h3>
                    {!platform.is_active && (
                      <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">
                        Masqué
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500">{platform.transfer_type}</p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                {/* Toggle visibility */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleToggle(platform)}
                  className={platform.is_active ? 'text-gray-500' : 'text-gray-400'}
                  title={platform.is_active ? 'Masquer' : 'Afficher'}
                >
                  {platform.is_active ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                </Button>

                {/* Edit */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleOpenForm(platform)}
                  className="text-[#2eb382]"
                >
                  <Edit className="h-4 w-4" />
                </Button>

                {/* Delete */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setDeleteConfirm(platform)}
                  className="text-red-500 hover:text-red-700"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>

                {/* Expand */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setExpandedPlatform(expandedPlatform === platform.id ? null : platform.id)}
                >
                  {expandedPlatform === platform.id ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>

            {/* Expanded Details */}
            {expandedPlatform === platform.id && (
              <div className="px-4 pb-4 border-t border-gray-100">
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-medium text-gray-700 mb-2">Étapes de transfert</h4>
                    <ol className="space-y-1 text-sm text-gray-600">
                      {platform.steps?.map((step, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <span className="w-5 h-5 bg-[#67ffbb]/30 text-[#2eb382] rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">
                            {i + 1}
                          </span>
                          {step}
                        </li>
                      ))}
                    </ol>
                  </div>

                  <div>
                    <h4 className="font-medium text-gray-700 mb-2">Après le transfert</h4>
                    <ul className="space-y-1 text-sm text-gray-600">
                      {platform.after_transfer?.map((item, i) => (
                        <li key={i}>• {item}</li>
                      ))}
                    </ul>
                    
                    {platform.note && (
                      <div className={`mt-3 p-2 rounded text-sm ${
                        platform.note_type === 'success' ? 'bg-green-50 text-green-800' :
                        platform.note_type === 'warning' ? 'bg-amber-50 text-amber-800' :
                        'bg-blue-50 text-blue-800'
                      }`}>
                        {platform.note}
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-4">
                  <a
                    href={platform.login_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm text-[#2eb382] hover:underline"
                  >
                    <ExternalLink className="h-4 w-4" />
                    {platform.login_url}
                  </a>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Add/Edit Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingPlatform ? 'Modifier la plateforme' : 'Ajouter une plateforme'}
            </DialogTitle>
            <DialogDescription>
              {editingPlatform 
                ? 'Modifiez les informations de la plateforme de billets.'
                : 'Ajoutez une nouvelle plateforme de billets au formulaire de transfert.'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nom technique</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="ex: Ticketmaster"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Nom d'affichage</Label>
                <Input
                  value={formData.display_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, display_name: e.target.value }))}
                  placeholder="ex: Ticketmaster Canada"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Icône</Label>
                <div className="flex flex-wrap gap-2">
                  {iconOptions.map((icon) => (
                    <button
                      key={icon}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, icon }))}
                      className={`w-10 h-10 text-xl rounded-lg border-2 transition-colors ${
                        formData.icon === icon 
                          ? 'border-[#67ffbb] bg-[#67ffbb]/20' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {icon}
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <Label>Type de transfert</Label>
                <Input
                  value={formData.transfer_type}
                  onChange={(e) => setFormData(prev => ({ ...prev, transfer_type: e.target.value }))}
                  placeholder="ex: Transfert natif compte → compte"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>URL de connexion</Label>
              <Input
                type="url"
                value={formData.login_url}
                onChange={(e) => setFormData(prev => ({ ...prev, login_url: e.target.value }))}
                placeholder="https://..."
                required
              />
            </div>

            {/* Steps */}
            <div className="space-y-2">
              <Label>Étapes de transfert</Label>
              {formData.steps.map((step, index) => (
                <div key={index} className="flex items-center gap-2">
                  <span className="w-6 h-6 bg-[#67ffbb]/30 text-[#2eb382] rounded-full flex items-center justify-center text-xs font-bold">
                    {index + 1}
                  </span>
                  <Input
                    value={step}
                    onChange={(e) => handleStepChange('steps', index, e.target.value)}
                    placeholder={`Étape ${index + 1}`}
                    className="flex-1"
                  />
                  {formData.steps.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveStep('steps', index)}
                      className="text-red-500"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => handleAddStep('steps')}
                className="w-full border-dashed"
              >
                <Plus className="h-4 w-4 mr-2" />
                Ajouter une étape
              </Button>
            </div>

            {/* After Transfer */}
            <div className="space-y-2">
              <Label>Après le transfert</Label>
              {formData.after_transfer.map((item, index) => (
                <div key={index} className="flex items-center gap-2">
                  <span className="text-gray-400">•</span>
                  <Input
                    value={item}
                    onChange={(e) => handleStepChange('after_transfer', index, e.target.value)}
                    placeholder={`Point ${index + 1}`}
                    className="flex-1"
                  />
                  {formData.after_transfer.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveStep('after_transfer', index)}
                      className="text-red-500"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => handleAddStep('after_transfer')}
                className="w-full border-dashed"
              >
                <Plus className="h-4 w-4 mr-2" />
                Ajouter un point
              </Button>
            </div>

            {/* Note */}
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2 space-y-2">
                <Label>Note (optionnel)</Label>
                <Input
                  value={formData.note}
                  onChange={(e) => setFormData(prev => ({ ...prev, note: e.target.value }))}
                  placeholder="ex: Tu peux annuler tant que ce n'est pas accepté."
                />
              </div>
              <div className="space-y-2">
                <Label>Type de note</Label>
                <select
                  value={formData.note_type}
                  onChange={(e) => setFormData(prev => ({ ...prev, note_type: e.target.value }))}
                  className="w-full h-10 px-3 rounded-md border border-gray-200"
                >
                  {noteTypes.map((type) => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Active Status */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <Label>Plateforme active</Label>
                <p className="text-xs text-gray-500">Visible dans le formulaire de transfert</p>
              </div>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseForm}>
                Annuler
              </Button>
              <Button 
                type="submit" 
                className="bg-[#67ffbb] hover:bg-[#4de6a8] text-gray-900 font-semibold"
                disabled={saving}
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Enregistrement...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Enregistrer
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Supprimer la plateforme ?</DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir supprimer "{deleteConfirm?.display_name || deleteConfirm?.name}" ? 
              Cette action est irréversible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
              Annuler
            </Button>
            <Button 
              variant="destructive" 
              onClick={() => handleDelete(deleteConfirm)}
            >
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PlatformManagement;
